/**
 * printReportUtil.js
 * -------------------
 * Reusable Enterprise Print Engine for all reporting and list modules.
 *
 * Capabilities:
 * - Bypasses browser popup blockers via synchronous window initialization.
 * - Injects professional print styles (@page landscape, header repeating, alternating shading).
 * - Enforces safety threshold (default 1,000 rows):
 *     <= threshold: Automatically formats table and triggers window.print().
 *     > threshold:  Displays a clear capacity banner with 1-click triggers for Background PDF/Excel export
 *                   and an optional "Print Anyway" override.
 * - Handles currency alignment, total rows, and date timestamps.
 */

export function escapeHtml(value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

export function formatCurrency(amount) {
  const num = parseFloat(amount)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Synchronously opens a dedicated print window inside user click handler
 * to prevent modern browsers from blocking the popup.
 */
export function createPrintWindow(title = 'Print Report') {
  try {
    const printWindow = window.open('about:blank', '_blank', 'width=1200,height=850,resizable=yes,scrollbars=yes')
    if (!printWindow) return null

    printWindow.document.open()
    printWindow.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} - Preparing...</title>
  <style>
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      margin: 0;
      padding: 48px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 60vh;
      background: #f8fafc;
      color: #1e293b;
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #e2e8f0;
      border-top-color: #00327d;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin-bottom: 16px;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .title { font-size: 18px; font-weight: 600; color: #00327d; margin-bottom: 6px; }
    .sub { font-size: 13px; color: #64748b; }
  </style>
</head>
<body>
  <div class="spinner"></div>
  <div class="title">Preparing Report Preview</div>
  <div class="sub">Fetching data from server...</div>
</body>
</html>`)
    printWindow.document.close()
    return printWindow
  } catch (err) {
    console.error('[printReportUtil] Error opening print window:', err)
    return null
  }
}

/**
 * Formats and renders the full printable HTML document into printWindow.
 */
function renderPrintDocument({
  printWindow,
  title,
  subtitle,
  metadata = {},
  columns = [],
  rows = [],
  totalAmount = 0,
  autoPrint = true,
}) {
  const generatedAt = new Date().toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  const headerCellsHtml = columns
    .map((col) => {
      const isRight = col.align === 'right' || col.key?.toLowerCase().includes('amount')
      const alignStyle = isRight ? 'text-align: right;' : 'text-align: left;'
      return `<th style="${alignStyle}">${escapeHtml(col.label || col.key)}</th>`
    })
    .join('')

  const bodyRowsHtml = rows
    .map((row, idx) => {
      const isEven = idx % 2 === 1
      const bg = isEven ? 'background-color: #f8fafc;' : 'background-color: #ffffff;'
      const cellsHtml = columns
        .map((col) => {
          let val = row[col.key]
          const isRight = col.align === 'right' || col.key?.toLowerCase().includes('amount')
          if (col.format === 'currency' || col.key?.toLowerCase().includes('amount')) {
            val = formatCurrency(val)
          } else if (col.format === 'date' && val) {
            val = String(val).split('T')[0]
          }
          const alignStyle = isRight ? 'text-align: right;' : 'text-align: left;'
          return `<td style="${alignStyle}">${escapeHtml(val ?? '')}</td>`
        })
        .join('')
      return `<tr style="${bg}">${cellsHtml}</tr>`
    })
    .join('')

  // Build Grand Total Row
  const totalCellsHtml = columns
    .map((col, idx) => {
      if (idx === 0) {
        return '<td style="font-weight: bold; text-align: left;">Total</td>'
      }
      if (col.key?.toLowerCase().includes('amount') || col.isTotal) {
        return `<td style="font-weight: bold; text-align: right;">₹${formatCurrency(totalAmount)}</td>`
      }
      return '<td></td>'
    })
    .join('')

  const metadataHtml = Object.entries(metadata)
    .filter(([_, v]) => Boolean(v))
    .map(([k, v]) => `<span class="meta-item"><strong>${escapeHtml(k)}:</strong> ${escapeHtml(v)}</span>`)
    .join(' &bull; ')

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page {
      size: landscape;
      margin: 12mm 10mm 12mm 10mm;
    }
    *, *:before, *:after { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      margin: 0;
      padding: 24px;
      color: #0f172a;
      background: #ffffff;
      font-size: 11px;
      line-height: 1.4;
    }
    .no-print-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 12px 18px;
      margin-bottom: 20px;
    }
    .btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 600;
      border-radius: 6px;
      cursor: pointer;
      border: none;
      transition: background 0.15s ease;
    }
    .btn-primary { background: #00327d; color: #ffffff; }
    .btn-primary:hover { background: #002255; }
    .btn-secondary { background: #e2e8f0; color: #334155; margin-left: 8px; }
    .btn-secondary:hover { background: #cbd5e1; }
    
    .report-header {
      margin-bottom: 16px;
      border-bottom: 2px solid #00327d;
      padding-bottom: 10px;
    }
    .report-title {
      font-size: 20px;
      font-weight: 700;
      color: #00327d;
      margin: 0 0 4px 0;
    }
    .report-sub {
      font-size: 12px;
      color: #475569;
      margin: 0 0 8px 0;
    }
    .report-meta {
      font-size: 11px;
      color: #64748b;
    }
    .meta-item { display: inline-block; }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
      font-size: 10.5px;
    }
    thead {
      display: table-header-group;
    }
    tfoot {
      display: table-footer-group;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    th, td {
      border: 1px solid #cbd5e1;
      padding: 6px 8px;
      vertical-align: top;
    }
    th {
      background-color: #00327d !important;
      color: #ffffff !important;
      font-weight: 600;
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    tfoot td {
      background-color: #e2e8f0 !important;
      font-weight: bold;
      border-top: 2px solid #00327d;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    @media print {
      body { padding: 0; }
      .no-print-bar { display: none !important; }
      th {
        background-color: #00327d !important;
        color: #ffffff !important;
      }
    }
  </style>
</head>
<body>
  <div class="no-print-bar">
    <div>
      <strong>Print Preview</strong> &bull; Showing ${rows.length} records &bull; Generated ${escapeHtml(generatedAt)}
    </div>
    <div>
      <button class="btn btn-primary" onclick="window.print()">Print Document</button>
      <button class="btn btn-secondary" onclick="window.close()">Close</button>
    </div>
  </div>

  <div class="report-header">
    <h1 class="report-title">${escapeHtml(title)}</h1>
    ${subtitle ? `<p class="report-sub">${escapeHtml(subtitle)}</p>` : ''}
    <div class="report-meta">
      ${metadataHtml ? `<div>${metadataHtml}</div>` : ''}
      <div style="margin-top: 2px;">Generated on: <strong>${escapeHtml(generatedAt)}</strong> | Total Records: <strong>${rows.length}</strong></div>
    </div>
  </div>

  <table>
    <thead>
      <tr>${headerCellsHtml}</tr>
    </thead>
    <tbody>
      ${bodyRowsHtml}
    </tbody>
    <tfoot>
      <tr>${totalCellsHtml}</tr>
    </tfoot>
  </table>

  ${autoPrint ? `<script>window.onload = function() { setTimeout(function() { window.print(); }, 250); };</script>` : ''}
</body>
</html>`

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
}

/**
 * Renders an enterprise guardrail notice if the dataset exceeds the browser printing threshold.
 */
function renderThresholdNotice({
  printWindow,
  title,
  rowCount,
  threshold,
  onExportPdf,
  onExportExcel,
  renderAnywayCallback,
}) {
  const estPages = Math.ceil(rowCount / 25)

  // Attach callbacks to the window object so action buttons can invoke them
  printWindow.__printAnyway = () => {
    renderAnywayCallback()
  }
  printWindow.__triggerExportPdf = () => {
    if (onExportPdf) onExportPdf()
    printWindow.close()
  }
  printWindow.__triggerExportExcel = () => {
    if (onExportExcel) onExportExcel()
    printWindow.close()
  }

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Large Report Notice - ${escapeHtml(title)}</title>
  <style>
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      margin: 0;
      padding: 40px 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 80vh;
      background: #f8fafc;
      color: #0f172a;
    }
    .card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 36px;
      max-width: 580px;
      width: 100%;
      box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.08);
      text-align: center;
    }
    .icon {
      font-size: 44px;
      margin-bottom: 12px;
    }
    h2 {
      margin: 0 0 10px;
      font-size: 20px;
      color: #00327d;
    }
    p {
      margin: 0 0 16px;
      font-size: 13.5px;
      color: #475569;
      line-height: 1.5;
    }
    .stats-box {
      background: #f1f5f9;
      border-radius: 8px;
      padding: 14px;
      margin: 16px 0 24px;
      display: flex;
      justify-content: space-around;
      border: 1px solid #cbd5e1;
    }
    .stat-item { text-align: center; }
    .stat-val { font-size: 18px; font-weight: 700; color: #00327d; }
    .stat-lbl { font-size: 11px; color: #64748b; text-transform: uppercase; margin-top: 2px; }

    .btn-group {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .btn {
      display: block;
      width: 100%;
      padding: 12px 18px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      transition: all 0.15s ease;
    }
    .btn-primary { background: #00327d; color: #ffffff; }
    .btn-primary:hover { background: #002255; }
    .btn-excel { background: #107c41; color: #ffffff; }
    .btn-excel:hover { background: #0b5a2f; }
    .btn-link { background: transparent; color: #64748b; font-size: 12.5px; text-decoration: underline; margin-top: 6px; }
    .btn-link:hover { color: #0f172a; }
  </style>
</head>
<body>
  <div class="card">
    <div class="icon">📊</div>
    <h2>Large Report Notice (${rowCount.toLocaleString('en-IN')} Records)</h2>
    <p>
      Direct browser printing is optimized for up to <strong>${threshold.toLocaleString('en-IN')} records</strong>. 
      Rendering ${rowCount.toLocaleString('en-IN')} records directly in the browser may freeze your tab or run out of memory.
    </p>

    <div class="stats-box">
      <div class="stat-item">
        <div class="stat-val">${rowCount.toLocaleString('en-IN')}</div>
        <div class="stat-lbl">Total Records</div>
      </div>
      <div class="stat-item">
        <div class="stat-val">~${estPages}</div>
        <div class="stat-lbl">Est. Paper Pages</div>
      </div>
      <div class="stat-item">
        <div class="stat-val">${threshold.toLocaleString('en-IN')}</div>
        <div class="stat-lbl">Print Threshold</div>
      </div>
    </div>

    <p style="font-size: 12.5px; color: #64748b; margin-bottom: 20px;">
      Recommended: Use background export to generate a clean, downloadable file in the background without affecting your system.
    </p>

    <div class="btn-group">
      <button class="btn btn-primary" onclick="window.__triggerExportPdf()">
        📥 Download Formatted PDF Report (Background)
      </button>
      <button class="btn btn-excel" onclick="window.__triggerExportExcel()">
        📊 Download Excel Spreadsheet (Background)
      </button>
      <button class="btn btn-link" onclick="window.__printAnyway()">
        Print all ${rowCount.toLocaleString('en-IN')} records directly in browser anyway
      </button>
      <button class="btn btn-link" style="text-decoration: none; color: #94a3b8;" onclick="window.close()">
        Cancel and Close
      </button>
    </div>
  </div>
</body>
</html>`

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
}

/**
 * Main facade function to execute end-to-end report printing.
 *
 * @param {Object} options
 * @param {string} options.title - Report title
 * @param {string} options.subtitle - Optional subtitle
 * @param {Object} options.metadata - Key-value metadata (e.g. date range, employee)
 * @param {Array}  options.columns - Table column definitions [{ key, label, align, format }]
 * @param {Function} options.fetchReportData - Async function returning { items: [], total_amount: 0 }
 * @param {Function} options.onExportPdf - Callback to trigger background PDF export
 * @param {Function} options.onExportExcel - Callback to trigger background Excel/CSV export
 * @param {number} options.threshold - Max rows before guardrail is shown (default 1000)
 */
let isPrintExecutionActive = false

export async function executePrintReport({
  title,
  subtitle,
  metadata = {},
  columns = [],
  fetchReportData,
  onExportPdf,
  onExportExcel,
  threshold = 1000,
}) {
  if (isPrintExecutionActive) {
    console.warn('[printReportUtil] Print execution already active, ignoring duplicate trigger.')
    return
  }
  isPrintExecutionActive = true

  // Step 1: Open popup synchronously (user gesture)
  const printWindow = createPrintWindow(title)
  if (!printWindow) {
    isPrintExecutionActive = false
    appToast.error('Pop-up window was blocked. Please allow pop-ups for this site to print reports.')
    return
  }

  try {
    // Step 2: Fetch report data asynchronously
    const data = await fetchReportData()
    const items = Array.isArray(data) ? data : data?.items || []
    const totalAmount = data?.total_amount ?? data?.totalAmount ?? 0

    if (items.length === 0) {
      printWindow.document.body.innerHTML = `
        <div style="font-family: system-ui; text-align: center; padding: 48px; color: #64748b;">
          <h3 style="color: #00327d; margin-bottom: 8px;">No Records Found</h3>
          <p>There are no records matching the selected filter criteria.</p>
          <button style="padding: 8px 16px; border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer;" onclick="window.close()">Close Window</button>
        </div>
      `
      return
    }

    // Step 3: Check safety threshold
    if (items.length > threshold) {
      renderThresholdNotice({
        printWindow,
        title,
        rowCount: items.length,
        threshold,
        onExportPdf,
        onExportExcel,
        renderAnywayCallback: () => {
          renderPrintDocument({
            printWindow,
            title,
            subtitle,
            metadata,
            columns,
            rows: items,
            totalAmount,
            autoPrint: true,
          })
        },
      })
    } else {
      // Step 4: Render directly and auto-print
      renderPrintDocument({
        printWindow,
        title,
        subtitle,
        metadata,
        columns,
        rows: items,
        totalAmount,
        autoPrint: true,
      })
    }
  } catch (err) {
    console.error('[printReportUtil] Failed to fetch report data:', err)
    if (printWindow && !printWindow.closed) {
      printWindow.document.body.innerHTML = `
        <div style="font-family: system-ui; text-align: center; padding: 48px; color: #dc2626;">
          <h3 style="margin-bottom: 8px;">Failed to Load Report</h3>
          <p style="color: #64748b;">${escapeHtml(err?.message || 'An error occurred while generating the print preview.')}</p>
          <button style="padding: 8px 16px; border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; margin-top: 12px;" onclick="window.close()">Close</button>
        </div>
      `
    }
  } finally {
    setTimeout(() => {
      isPrintExecutionActive = false
    }, 600)
  }
}
